// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout';
import EntityGuard from '../../../components/EntityGuard';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useDashboardApi } from '../../../store/api/hooks/dashboardApiHook';
import ReportFilterBar from '../../../components/ReportFilterBar';
import Button from '../../../components/elements/button';
import { downloadExcel } from '../../../_common/functions';
import toast from 'react-hot-toast';
import { useAssetsApi } from '../../../store/api/hooks/assetsApiHook';

function currencyCell(params: any) {
  const val = params.value;
  if (val == null) return '';
  return <span>${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
}

function hoursCell(params: any) {
  const val = params.value;
  if (val == null) return '';
  return <span>{Number(val).toLocaleString()} hrs</span>;
}

const AssetsTimeCostReport: React.FC = () => {
  const dashboardApi = useDashboardApi();
  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [summary, setSummary] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const assetsAPI = useAssetsApi();
  
    const GetAssetStatusList = (): Promise<any[]> => {
      return new Promise((resolve, reject) => {
        assetsAPI.GetAssetStatusList(
          {},
          (resp: any) => {
            const _statusList = resp.assetStatusList
              .filter((o: any) => o.isVisible)
              .map((item: any) => ({
                label: item.statusName,
                value: item.statusID,
              }));
            resolve(_statusList);
          },
          (error: any) => {
            console.log("error =>", error);
            toast.error(error);
            reject([]);
          }
        );
      });
    };

  const fetchData = useCallback(async (pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.facility) params.FacilityIDs = filterParams.facility;
    if (filterParams.subFacility) params.SubFacilityIDs = filterParams.subFacility;
    if (filterParams.assetGroup) params.AssetGroupIDs = filterParams.assetGroup;
    if (filterParams.department) params.DepartmentIDs = filterParams.department;
    if (filterParams.status) params.StatusIDs = filterParams.status;
    if (filterParams.startDate) params.StartDate = filterParams.startDate;
    if (filterParams.endDate) params.EndDate = filterParams.endDate;

    const statusList = await GetAssetStatusList();

    dashboardApi.getAssetsTimeCostReport(
      params,
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setSummary(d?.summary);
        setTotalRecords(d?.summary?.totalAssets || 0);
        setFilterOptions([
          { key: 'facility', label: 'Facility', options: (d?.facilities || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'subFacility', label: 'Sub Facility', options: (d?.subFacilities || []).filter((f: any) => f.name && f.name.trim()).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'assetGroup', label: 'Asset Group', options: (d?.assetGroups || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'department', label: 'Department', options: (d?.departments || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'status', label: 'Status', options: statusList},
        ]);
      },
      (error: any) => { console.error('Failed to fetch time cost report:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage]);

  const handleApplyFilter = (filterMap: Record<string, string>, startDate?: string, endDate?: string) => {
    const newFilters = { ...filterMap, startDate, endDate };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchData(1, newFilters);
  };

  const handleExport = () => {
    const params: any = { PageSize: null, PageNumber: null };
    if (filters.facility) params.FacilityIDs = filters.facility;
    if (filters.subFacility) params.SubFacilityIDs = filters.subFacility;
    if (filters.assetGroup) params.AssetGroupIDs = filters.assetGroup;
    if (filters.department) params.DepartmentIDs = filters.department;
    if (filters.status) params.StatusIDs = filters.status;
    if (filters.startDate) params.StartDate = filters.startDate;
    if (filters.endDate) params.EndDate = filters.endDate;
    setIsExporting(true);
    dashboardApi.exportAssetsTimeCostReportToXLS(
      params,
      (resp: any) => { downloadExcel(resp, 'assets_time_cost_report'); toast.success('Exported successfully!'); setIsExporting(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExporting(false); }
    );
  };

  const fmtCurrency = (val: number) => '$' + Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const columnDefs = [
    { headerName: 'Asset Name', field: 'assetName', headerClass: 'bg-[#f0f0f0]', minWidth: 200, flex: 1, cellRenderer: titleCell, headerTooltip: 'Asset Name' },
    { headerName: 'Asset Code', field: 'assetCode', headerClass: 'bg-[#f0f0f0]', width: 160, cellRenderer: titleCell, headerTooltip: 'Asset Code' },
    { headerName: 'Facility', field: 'facilityName', headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Facility' },
    { headerName: 'Sub Facility', field: 'subFacilityName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Sub Facility' },
    { headerName: 'Asset Group', field: 'assetGroupName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Asset Group' },
    { headerName: 'Department', field: 'departmentName', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'Department' },
    { headerName: 'Repair Hours', field: 'totalRepairHours', headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: hoursCell, headerTooltip: 'Total Repair Hours' },
    { headerName: 'Parts Cost', field: 'totalPartsCost', headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: currencyCell, headerTooltip: 'Total Parts Cost' },
    { headerName: 'Total WRs', field: 'totalWRs', headerClass: 'bg-[#f0f0f0]', width: 110, type: 'rightAligned', cellRenderer: titleCell, headerTooltip: 'Total Work Requests' },
    { headerName: 'Avg Cost/WR', field: 'avgCostPerWR', headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: currencyCell, headerTooltip: 'Average Cost per Work Request' },
  ];

  return (
    <Layout>
      <EntityGuard entity="Assets Time and Cost">
      <Header title="Assets Time and Cost Report" back="/reports">
        <Header.Right>
          <Button theme='primary' onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        <ReportFilterBar availableFilters={filterOptions} onApply={handleApplyFilter} />
        {summary && (
          <div className="bg-white rounded-lg mb-4 flex items-center gap-6 flex-wrap text-[13px]" style={{ padding: '12px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <span style={{ color: '#111827', fontWeight: 600 }}>Assets: {summary.totalAssets}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#111827', fontWeight: 600 }}>Repair Hours: {Number(summary.totalRepairHours || 0).toLocaleString()} hrs</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#2B5A2E', fontWeight: 600 }}>Parts Cost: {fmtCurrency(summary.totalPartsCost)}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#111827', fontWeight: 600 }}>WRs: {summary.totalWRs}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#d97706', fontWeight: 600 }}>Avg Cost/WR: {fmtCurrency(summary.avgCostPerWR)}</span>
          </div>
        )}
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
      </div>
      </EntityGuard>
    </Layout>
  );
};

export default AssetsTimeCostReport;
